import React from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../ui/shadcn';
import { Button } from '@kioskgaming/ui';
import { LoadingSpinner } from '../../ui/Loading';
import {
  IntegrationLogTab,
  IntegrationOutboundLogDetail,
  IntegrationWebhookLogDetail,
} from '../../../types';

interface IntegrationLogDetailSheetProps {
  open: boolean;
  onClose: () => void;
  tab: IntegrationLogTab;
  log: IntegrationOutboundLogDetail | IntegrationWebhookLogDetail | null;
  isLoading: boolean;
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  const text =
    value == null
      ? '—'
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <Button type="button" variant="ghost" size="sm" onClick={handleCopy}>
          <Copy className="h-3.5 w-3.5 mr-1" />
          Copy
        </Button>
      </div>
      <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-xs font-mono whitespace-pre-wrap break-all">
        {text}
      </pre>
    </div>
  );
}

const IntegrationLogDetailSheet: React.FC<IntegrationLogDetailSheetProps> = ({
  open,
  onClose,
  tab,
  log,
  isLoading,
}) => {
  const isWebhook = tab === 'webhooks';
  const outbound = !isWebhook ? (log as IntegrationOutboundLogDetail | null) : null;
  const webhook = isWebhook ? (log as IntegrationWebhookLogDetail | null) : null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>API log detail</SheetTitle>
          <SheetDescription>
            Sensitive data may be masked. Super admin access only.
          </SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {!isLoading && !log && (
          <p className="text-sm text-muted-foreground py-8">Log not found.</p>
        )}

        {!isLoading && log && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">ID</span>
                <p className="font-mono break-all">{String(log.id)}</p>
              </div>
              {log.providerCode && (
                <div>
                  <span className="text-muted-foreground">Provider</span>
                  <p>{log.providerCode}</p>
                </div>
              )}
              {log.apiMethod && (
                <div>
                  <span className="text-muted-foreground">Method</span>
                  <p>{log.apiMethod}</p>
                </div>
              )}
              {log.transactionId && (
                <div>
                  <span className="text-muted-foreground">Transaction ID</span>
                  <p className="font-mono text-xs break-all">{log.transactionId}</p>
                </div>
              )}
              {'success' in log && log.success !== undefined && (
                <div>
                  <span className="text-muted-foreground">Success</span>
                  <p>{log.success ? 'Yes' : 'No'}</p>
                </div>
              )}
              {'processed' in log && log.processed !== undefined && (
                <div>
                  <span className="text-muted-foreground">Processed</span>
                  <p>{log.processed ? 'Yes' : 'No'}</p>
                </div>
              )}
            </div>

            {isWebhook && webhook && (
              <>
                <JsonBlock label="Raw data" value={webhook.rawData} />
                <JsonBlock label="Raw headers" value={webhook.rawHeaders} />
                {webhook.rawBody && <JsonBlock label="Raw body" value={webhook.rawBody} />}
                {webhook.errorMessage && (
                  <JsonBlock label="Error" value={webhook.errorMessage} />
                )}
              </>
            )}

            {!isWebhook && outbound && (
              <>
                {outbound.requestUrl && (
                  <JsonBlock label="Request URL" value={outbound.requestUrl} />
                )}
                <JsonBlock label="Request headers" value={outbound.requestHeaders} />
                <JsonBlock label="Request body" value={outbound.requestBody} />
                <JsonBlock label="Response headers" value={outbound.responseHeaders} />
                <JsonBlock label="Response body" value={outbound.responseBody} />
                {outbound.errorMessage && (
                  <JsonBlock label="Error message" value={outbound.errorMessage} />
                )}
                {outbound.errorStack && (
                  <JsonBlock label="Error stack" value={outbound.errorStack} />
                )}
                <JsonBlock label="Metadata" value={outbound.metadata} />
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default IntegrationLogDetailSheet;
